import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AuthorsDashboard.module.css";
import { Edit, Trash, Plus, Search, User, Globe } from "lucide-react";
import Loading from "../../../common/HousGlass Loader/Loading";
import { getAuthors, deleteAuthor } from "../../../api/admin/adminAuthors";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};

// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Search Component
const SearchBar = ({ onSearch, onLanguageFilter }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleLanguageFilter = (e) => {
    const value = e.target.value;
    setLanguageFilter(value);
    onLanguageFilter(value);
  };

  return (
    <div className={styles.searchContainer}>
      <input
        type="text"
        placeholder="Search by name or bio..."
        value={searchTerm}
        onChange={handleSearch}
        className={styles.searchInput}
      />
      <Search className={styles.searchIcon} size={18} />
      
      <select
        value={languageFilter}
        onChange={handleLanguageFilter}
        className={styles.languageFilter}
      >
        <option value="">All Languages</option>
        <option value="english">English</option>
        <option value="hindi">Hindi</option>
        <option value="urdu">Urdu</option>
        <option value="punjabi">Punjabi</option>
      </select>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel, isLoading }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>

        <div className={styles.modalButtons}>
          <button 
            className={styles.modalButton} 
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Confirm'}
          </button>
          <button 
            className={styles.modalButton} 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AuthorsDashboard = () => {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const itemsPerPage = 12;

  const loadAuthors = async (page = 1, search = '', language = '') => {
    try {
      setIsLoading(true);
      const data = await getAuthors(page, itemsPerPage, search, language);
      setAuthors(data.authors);
      setTotalPages(data.pagination.totalPages);
      setTotalItems(data.pagination.totalItems);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading authors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAuthors(1, searchTerm, languageFilter);
  }, [searchTerm, languageFilter]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleLanguageFilter = (language) => {
    setLanguageFilter(language);
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit-author/${id}`);
  };

  const handleAddAuthor = () => {
    navigate('/admin/add-author');
  };

  // Opens the confirmation modal instead of deleting immediately
  const confirmDelete = (id) => {
    setAuthorToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion
  const confirmDeleteAction = async () => {
    if (authorToDelete !== null) {
      try {
        setIsDeleting(true);
        await deleteAuthor(authorToDelete);
        setAuthorToDelete(null);
        setShowModal(false);
        // Reload the current page
        loadAuthors(currentPage, searchTerm, languageFilter);
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setAuthorToDelete(null);
    setShowModal(false);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      loadAuthors(currentPage + 1, searchTerm, languageFilter);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      loadAuthors(currentPage - 1, searchTerm, languageFilter);
    }
  };

  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (!authors || authors.length === 0) {
    return (
      <div className={styles.empty}>
        <p>No authors found</p>
        <button 
          className={styles.addButton}
          onClick={handleAddAuthor}
        >
          <Plus size={20} />
          Add Author
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>Author Management</h1>
        <SearchBar onSearch={handleSearch} onLanguageFilter={handleLanguageFilter} />
        <div className={styles.buttonGroup}>
          <button 
            className={styles.addButton}
            onClick={handleAddAuthor}
          >
            <Plus size={20} />
            Add Author
          </button>
        </div>
      </div>

      <div className={styles.authorsContainer}>
        {authors.map((author) => (
          <div key={author.id} className={styles.authorItem}>
            <div className={styles.authorImageContainer}>
              <img
                src={author.profile_image_url || "/images/placeholders/author-profile.png"}
                alt={author.name}
                className={styles.authorImage}
              />
            </div>
            <div className={styles.authorInfo}>
              <h3 className={styles.authorName}>{author.name}</h3>
              <p className={styles.authorBio}>{author.bio || 'No bio available'}</p>
              <div className={styles.authorMeta}>
                <span className={styles.language}>
                  <Globe size={14} />
                  {author.language || 'english'}
                </span>
                <span className={styles.followers}>
                  <User size={14} />
                  {author.followers_count || 0} followers
                </span>
              </div>
            </div>
            <div className={styles.iconsOverlay}>
              <EditIconComponent id={author.id} onEdit={handleEdit} />
              <DeleteIconComponent id={author.id} onDelete={confirmDelete} />
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={goToPrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages} ({totalItems} total)
          </span>
          <button
            className={styles.paginationButton}
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message={`Are you sure you want to delete "${authors.find(a => a.id === authorToDelete)?.name}"?`}
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default AuthorsDashboard;